import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "oaiMainContentFullScreen flex flex-col" }
const _hoisted_2 = { class: "border-b border-gray-200 bg-gray-50 sticky top-0 z-10" }
const _hoisted_3 = { class: "flex justify-between items-center py-2 sm:py-3 mx-6" }

import { PlusIcon } from "@heroicons/vue/24/outline";
import { useInfiniteScroll } from "@vueuse/core";
import mobile from "is-mobile";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import SelectList from "shared/components/other/OaiListbox.vue";
import { ProjectExtendedData, ProjectStatus, SimplifiedProject } from "shared/types/Project";
import { WeatherItem } from "shared/types/Weather";
import {
  useExtendedDataForProjects,
  useThumbnailUrls,
  useThumbnailUrlsAll,
  useWeatherForProjects,
} from "@/composables/project";
import AddProjectModal from "@/views/dashboard/components/AddProjectModal.vue";
import ProjectCard from "@/views/dashboard/components/ProjectCard.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'Projects',
  setup(__props) {

const containerElement = ref<HTMLDivElement | null>(null);
// should be divisible by 2 and 3 so it fills the last row
const pageSize = ref(12);
const page = ref(1);

const selectedProjectStatus = ref<ProjectStatus | "all">("all");

const selectedProjectStatusOrNull = computed(() =>
  selectedProjectStatus.value === "all" ? null : selectedProjectStatus.value,
);

const store = useStore();
const { projectThumbnailUrls } = useThumbnailUrls(page, pageSize, selectedProjectStatusOrNull);
const { projectThumbnailUrlsAll } = useThumbnailUrlsAll(
  page,
  pageSize,
  selectedProjectStatusOrNull,
);
const { weatherForProjects } = useWeatherForProjects();
const { extendedDataForProjects } = useExtendedDataForProjects();

const allFilteredProjects = computed(() =>
  selectedProjectStatus.value === "all"
    ? projects
    : projects?.filter((project) => project.status === selectedProjectStatus.value),
);

const filteredProjects = computed(() =>
  allFilteredProjects.value?.filter((project, index) => index < page.value * pageSize.value),
);

useInfiniteScroll(
  containerElement,
  () => {
    page.value = page.value + 1;
  },
  {
    direction: "bottom",
    canLoadMore: () =>
      !!filteredProjects.value &&
      !!allFilteredProjects.value &&
      filteredProjects.value.length !== allFilteredProjects.value.length,
  },
);

const { t } = useI18n();
const isMobileDevice = mobile();

const projects = store.state.projects as SimplifiedProject[] | undefined;
const openAddProjectModal = ref(false);

const projectStatusOptions = [
  { value: "active", name: t("project.status.active") },
  { value: "completed", name: t("project.status.completed") },
  { value: "all", name: t(`dashboard.home.all_projects`) },
];

const thumbnailUrlsByProject = computed(() => {
  if (projectThumbnailUrlsAll.value.length === 0 || isMobileDevice) {
    return projectThumbnailUrls.value.reduce((acc, projectThumbnailUrl) => {
      acc[projectThumbnailUrl._id] = [projectThumbnailUrl.thumbnail_url];
      return acc;
    }, {} as Record<string, string[]>);
  } else {
    return projectThumbnailUrlsAll.value.reduce((acc, item) => {
      acc[item._id] = item.thumbnail_urls.map((urlItem) => urlItem.thumbnail_url);
      return acc;
    }, {} as Record<string, string[]>);
  }
});

const weatherByProject = computed(() =>
  weatherForProjects.value.reduce((acc, item) => {
    acc[item._id] = item.weather;
    return acc;
  }, {} as Record<string, WeatherItem>),
);
const extendedDataByProject = computed(() =>
  extendedDataForProjects.value.reduce((acc, extendedData) => {
    acc[extendedData._id] = extendedData;
    return acc;
  }, {} as Record<string, ProjectExtendedData>),
);

const handleProjectFilterChange = (event: ProjectStatus) => {
  page.value = 1;
  selectedProjectStatus.value = event;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", null, [
            _createVNode(SelectList, {
              minWidth: 220,
              options: projectStatusOptions,
              defaultSelected: 'all',
              "onUpdate:selected": handleProjectFilterChange
            })
          ]),
          _createElementVNode("button", {
            onClick: _cache[0] || (_cache[0] = ($event: any) => (openAddProjectModal.value = true)),
            type: "button",
            class: "hidden sm:flex items-center gap-1 rounded bg-yellow-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-yellow-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow-600"
          }, [
            _createVNode(_unref(PlusIcon), { class: "h-5 w-5" }),
            _createTextVNode(" " + _toDisplayString(_ctx.$t(`dashboard.home.add_project`)), 1)
          ])
        ])
      ]),
      _createElementVNode("div", {
        ref_key: "containerElement",
        ref: containerElement,
        class: "flex-1 pt-2 grid grid-cols-1 px-6 pb-10 lg:grid-cols-2 2xl:grid-cols-3 gap-6 xl:gap-6 overflow-auto"
      }, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredProjects.value, (project, index) => {
          return (_openBlock(), _createBlock(ProjectCard, {
            key: index,
            id: 'project-link-' + index,
            project: project,
            thumbnailUrls: thumbnailUrlsByProject.value[project._id],
            weather: weatherByProject.value[project._id],
            extendedData: extendedDataByProject.value[project._id]
          }, null, 8, ["id", "project", "thumbnailUrls", "weather", "extendedData"]))
        }), 128))
      ], 512)
    ]),
    _createVNode(AddProjectModal, {
      open: openAddProjectModal.value,
      onClose: _cache[1] || (_cache[1] = ($event: any) => (openAddProjectModal.value = false))
    }, null, 8, ["open"])
  ], 64))
}
}

})